import {
  DecoratedKitRequestLine,
  HandlingRequestDataLite,
  KitRequestLineDataAllocationLite,
  KitRequestLineDataLineLite,
  ShipmentLineDataLite,
  StockLotDataLite,
} from 'hooks/useGetDecoratedKitRequestLines';
import {
  HandlingRequestType,
  StockLocationLite,
  StockLotStatus,
} from 'types/inventory';
import { ChipColor } from 'ui-component/extended/Chip';
import _ from 'lodash';
import { StockLocationSchemaLite } from 'types/clientV2/stockLocations';
import { KitRequestSchema } from 'types/kitting';
import { typedGroupBy, typedKeyBy } from 'utils/typedLodash';
import { NON_REEL_TAPE_PACKAGES } from 'types/clientV2/stock';

export enum KitRequestAllocationStatus {
  Unavailable = 'unavailable',
  Expected = 'expected',
  OnHand = 'on_hand',
  Quarantined = 'quarantined',
  ReadyToPack = 'ready_to_pack',
  Packed = 'packed',
  Shipped = 'shipped',
  Delivered = 'delivered',
  NotInFacility = 'not_in_facility',
  Unknown = 'unknown',
  Ignored = 'ignored',
}

export const kitRequestAllocationStatusTextMapper: {
  [key in KitRequestAllocationStatus]: string;
} = {
  [KitRequestAllocationStatus.Unavailable]: 'UNAVAILABLE',
  [KitRequestAllocationStatus.Expected]: 'EXPECTED',
  [KitRequestAllocationStatus.OnHand]: 'ON HAND',
  [KitRequestAllocationStatus.Quarantined]: 'QUARANTINED',
  [KitRequestAllocationStatus.ReadyToPack]: 'READY TO PACK',
  [KitRequestAllocationStatus.Packed]: 'PACKED',
  [KitRequestAllocationStatus.Shipped]: 'SHIPPED',
  [KitRequestAllocationStatus.Delivered]: 'DELIVERED',
  [KitRequestAllocationStatus.NotInFacility]: 'NOT IN FACILITY',
  [KitRequestAllocationStatus.Unknown]: 'UNKNOWN',
  [KitRequestAllocationStatus.Ignored]: 'IGNORED',
};

export const kitRequestAllocationStatusTextMapperForProgram: {
  [key in KitRequestAllocationStatus]: string;
} = {
  [KitRequestAllocationStatus.Unavailable]: 'Not In Kit',
  [KitRequestAllocationStatus.Expected]: 'Added To Kit',
  [KitRequestAllocationStatus.OnHand]: 'Added To Kit',
  [KitRequestAllocationStatus.Quarantined]: 'Quarantined',
  [KitRequestAllocationStatus.ReadyToPack]: 'Added To Kit',
  [KitRequestAllocationStatus.Packed]: 'Packed',
  [KitRequestAllocationStatus.Shipped]: 'Shipped',
  [KitRequestAllocationStatus.Delivered]: 'Delivered',
  [KitRequestAllocationStatus.NotInFacility]: 'Not In Facility',
  [KitRequestAllocationStatus.Unknown]: 'Unknown',
  [KitRequestAllocationStatus.Ignored]: 'Ignored',
};

export const kitRequestAllocationStatusColorMapper: {
  [key in KitRequestAllocationStatus]: ChipColor;
} = {
  [KitRequestAllocationStatus.Unavailable]: ChipColor.error,
  [KitRequestAllocationStatus.Expected]: ChipColor.secondary,
  [KitRequestAllocationStatus.OnHand]: ChipColor.success,
  [KitRequestAllocationStatus.Quarantined]: ChipColor.error,
  [KitRequestAllocationStatus.ReadyToPack]: ChipColor.warning,
  [KitRequestAllocationStatus.Packed]: ChipColor.primary,
  [KitRequestAllocationStatus.Shipped]: ChipColor.primary,
  [KitRequestAllocationStatus.Delivered]: ChipColor.success,
  [KitRequestAllocationStatus.NotInFacility]: ChipColor.grey,
  [KitRequestAllocationStatus.Unknown]: ChipColor.grey,
  [KitRequestAllocationStatus.Ignored]: ChipColor.grey,
};

export const kitRequestAllocationStatusColorMapperForProgram: {
  [key in KitRequestAllocationStatus]: ChipColor;
} = {
  [KitRequestAllocationStatus.Unavailable]: ChipColor.error,
  [KitRequestAllocationStatus.Expected]: ChipColor.success,
  [KitRequestAllocationStatus.OnHand]: ChipColor.success,
  [KitRequestAllocationStatus.Quarantined]: ChipColor.error,
  [KitRequestAllocationStatus.ReadyToPack]: ChipColor.success,
  [KitRequestAllocationStatus.Packed]: ChipColor.primary,
  [KitRequestAllocationStatus.Shipped]: ChipColor.primary,
  [KitRequestAllocationStatus.Delivered]: ChipColor.success,
  [KitRequestAllocationStatus.NotInFacility]: ChipColor.grey,
  [KitRequestAllocationStatus.Unknown]: ChipColor.grey,
  [KitRequestAllocationStatus.Ignored]: ChipColor.grey,
};

export type StatusCount = {
  status: KitRequestAllocationStatus;
  quant: number;
};

export type KitRequestLineStats = {
  id: string;
  facility: StockLocationLite['id'] | null;
  targetQuantity: number | null;
  minimumQuantity: number | null;
  calculatedStatuses: KitRequestAllocationStatus[];
  statusCounts: StatusCount[];
};

type KitRequestStats = {
  totalLines: number;
  ignored: number;
  unavailable: number;
  expected: number;
  onHand: number;
  quarantined: number;
  readyToPack: number;
  packed: number;
  shipped: number;
  delivered: number;
  notInFacility: number;
};

export type CalculateKitRequestStatusReturn = {
  allocationStats: Record<string, KitRequestAllocationStatus>;
  lineStats: Record<string, KitRequestLineStats>;
  requestStats: KitRequestStats;
};

const calculateAllocationStatus = (
  allocation: KitRequestLineDataAllocationLite,
  kitRequestLine: DecoratedKitRequestLine,
  facility?: StockLocationLite['id']
): KitRequestAllocationStatus => {
  // case 1: Ignored - kit request line is ignored
  if (kitRequestLine.ignore) {
    return KitRequestAllocationStatus.Ignored;
  }

  // Case 2: Unavailable - no stock lot assigned
  if (!allocation.stockLot) {
    return KitRequestAllocationStatus.Unavailable;
  }

  const stockLot = allocation.stockLot;

  // Case 3: Expected - stock lot has status of 'expected'
  if (stockLot.status === StockLotStatus.EXPECTED) {
    return KitRequestAllocationStatus.Expected;
  }

  // Case 4: Check for shipment line
  // Shipment has to be shipped at too
  const matchingShipmentLine = kitRequestLine.shipmentLines.find(
    (shipmentLine) => shipmentLine.stockLot?.id === stockLot?.id
  );

  if (facility && stockLot.facility !== facility) {
    return KitRequestAllocationStatus.NotInFacility;
  }

  // Case 5: Shipped - shipment line exists and is not ingested
  if (matchingShipmentLine && !matchingShipmentLine.shipment.shippedAt) {
    return KitRequestAllocationStatus.Packed;
  }

  if (matchingShipmentLine && !matchingShipmentLine?.ingestedAt) {
    return KitRequestAllocationStatus.Shipped;
  }

  // Case 6: Delivered - shipment line exists and has been ingested
  if (matchingShipmentLine?.ingestedAt) {
    return KitRequestAllocationStatus.Delivered;
  }

  // Case 3: On Hand - stock lot has status of 'on hand'
  if (
    stockLot.status === StockLotStatus.ON_HAND ||
    stockLot.status === StockLotStatus.SMT_NO_PNP
  ) {
    // Check if fulfillable - has KIT handling request and no other pending handling requests
    const kitHandlingRequests = kitRequestLine.handlingRequests.filter(
      (hr) => hr.requestType === HandlingRequestType.KIT && !hr.completedAt
    );
    const otherPendingHandlingRequests = kitRequestLine.handlingRequests.filter(
      (hr) => hr.requestType !== HandlingRequestType.KIT && !hr.completedAt
    );

    if (
      kitHandlingRequests.length > 0 &&
      otherPendingHandlingRequests.length === 0
    ) {
      return KitRequestAllocationStatus.ReadyToPack;
    }

    return KitRequestAllocationStatus.OnHand;
  }

  // Case 7: Quarantined - stock lot has status of 'quarantined'
  if (stockLot.status === StockLotStatus.QUARANTINED) {
    return KitRequestAllocationStatus.Quarantined;
  }

  return KitRequestAllocationStatus.Unavailable;
};

// Helper function to determine if a line is fully allocated
const isLineFullyAllocated = (line: KitRequestLineStats): boolean => {
  const totalAllocated = line.statusCounts.reduce((sum, count) => {
    if (count.status === KitRequestAllocationStatus.Unavailable) return sum;
    return sum + count.quant;
  }, 0);

  // If we have a minimum quantity, use that as the threshold
  if (line.minimumQuantity !== null) {
    return totalAllocated >= line.minimumQuantity;
  }

  // If no minimum quantity but we have a target quantity, use that
  if (line.targetQuantity !== null) {
    return totalAllocated >= line.targetQuantity;
  }

  // If neither minimum nor target quantity is set, consider it not fully allocated
  return false;
};

export const getLinePrimaryStatus = (
  line: KitRequestLineStats
): KitRequestAllocationStatus => {
  // If line has any unavailable quantity and is not fully allocated, it's unavailable
  if (
    line.calculatedStatuses?.includes(KitRequestAllocationStatus.Unavailable) &&
    !isLineFullyAllocated(line)
  ) {
    return KitRequestAllocationStatus.Unavailable;
  }

  if (line.calculatedStatuses?.includes(KitRequestAllocationStatus.Delivered))
    return KitRequestAllocationStatus.Delivered;
  if (line.calculatedStatuses?.includes(KitRequestAllocationStatus.Shipped))
    return KitRequestAllocationStatus.Shipped;
  if (line.calculatedStatuses?.includes(KitRequestAllocationStatus.Packed))
    return KitRequestAllocationStatus.Packed;
  if (line.calculatedStatuses?.includes(KitRequestAllocationStatus.ReadyToPack))
    return KitRequestAllocationStatus.ReadyToPack;
  if (line.calculatedStatuses?.includes(KitRequestAllocationStatus.OnHand))
    return KitRequestAllocationStatus.OnHand;
  if (line.calculatedStatuses?.includes(KitRequestAllocationStatus.Expected))
    return KitRequestAllocationStatus.Expected;
  if (line.calculatedStatuses?.includes(KitRequestAllocationStatus.Quarantined))
    return KitRequestAllocationStatus.Quarantined;
  if (
    line.calculatedStatuses?.includes(KitRequestAllocationStatus.NotInFacility)
  )
    return KitRequestAllocationStatus.NotInFacility;
  if (line.calculatedStatuses?.includes(KitRequestAllocationStatus.Ignored))
    return KitRequestAllocationStatus.Ignored;
  return KitRequestAllocationStatus.Unavailable;
};

const countLinesByStatus = (
  lineStats: Record<string, KitRequestLineStats>,
  status: KitRequestAllocationStatus
): number =>
  Object.values(lineStats).filter(
    (line) => getLinePrimaryStatus(line) === status
  ).length;

export const calculateKitRequestStatus = (
  decoratedLines: DecoratedKitRequestLine[],
  facility?: StockLocationLite['id']
): CalculateKitRequestStatusReturn => {
  // Calculate allocation stats
  const allocationStats: Record<string, KitRequestAllocationStatus> = {};

  // Calculate line stats
  const lineStats: Record<string, KitRequestLineStats> = {};

  decoratedLines.forEach((line) => {
    // If line has no allocations and no shipment lines, mark it as unavailable
    if (!line.allocations.length && !line.shipmentLines.length) {
      lineStats[line.id] = {
        id: line.id,
        facility: line.stockLots[0]?.facility,
        targetQuantity: line.targetQuantity,
        minimumQuantity: line.minimumQuantity,
        calculatedStatuses: line.ignore
          ? [KitRequestAllocationStatus.Ignored]
          : [KitRequestAllocationStatus.Unavailable],
        statusCounts: [
          {
            status: line.ignore
              ? KitRequestAllocationStatus.Ignored
              : KitRequestAllocationStatus.Unavailable,
            quant: line.targetQuantity || 0,
          },
        ],
      };
      return;
    }

    const lineAllocations = line.allocations.map((allocation) => {
      const status = calculateAllocationStatus(allocation, line, facility);
      allocationStats[allocation.id] = status;
      return {
        status,
        quantity: allocation.quant,
      };
    });

    const shippedLines = _.filter(
      line.shipmentLines,
      (shipmentLine) => !!shipmentLine.shipment.shippedAt
    );

    const calculatedStatuses = lineAllocations.map(
      (allocation) => allocation.status
    );

    if (
      shippedLines.length > 0 &&
      !calculatedStatuses.includes(KitRequestAllocationStatus.Shipped)
    ) {
      calculatedStatuses.push(KitRequestAllocationStatus.Shipped);
    }

    const totalAllocated =
      lineAllocations.reduce(
        (sum, allocation) => sum + allocation.quantity,
        0
      ) + (shippedLines.length > 0 ? _.sumBy(shippedLines, 'shippedQuant') : 0);

    if (
      (line.minimumQuantity !== null &&
        totalAllocated < line.minimumQuantity) ||
      (line.minimumQuantity === null &&
        line.targetQuantity !== null &&
        totalAllocated < line.targetQuantity)
    ) {
      calculatedStatuses.push(KitRequestAllocationStatus.Unavailable);
    }

    lineStats[line.id] = {
      id: line.id,
      targetQuantity: line.targetQuantity,
      minimumQuantity: line.minimumQuantity,
      calculatedStatuses,
      facility: line.stockLots[0]?.facility,
      statusCounts: [
        ..._.chain(lineAllocations)
          .map('status')
          .uniq()
          .map((s) => ({
            status: s,
            quant: _.chain(lineAllocations)
              .filter({ status: s })
              .sumBy('quantity')
              .value(),
          }))
          .filter((s) => s.quant > 0)
          .value(),
        ...(shippedLines.length > 0
          ? [
              {
                status: KitRequestAllocationStatus.Shipped,
                quant: _.sumBy(shippedLines, 'shippedQuant'),
              },
            ]
          : []),
        ...((line.minimumQuantity !== null &&
          totalAllocated < line.minimumQuantity) ||
        (line.minimumQuantity === null &&
          line.targetQuantity !== null &&
          totalAllocated < line.targetQuantity)
          ? [
              {
                status: KitRequestAllocationStatus.Unavailable,
                quant:
                  (line.minimumQuantity ?? line.targetQuantity ?? 0) -
                  totalAllocated,
              },
            ]
          : []),
      ],
    };
  });

  const requestStats: KitRequestStats = {
    totalLines: decoratedLines.length,
    ignored: countLinesByStatus(lineStats, KitRequestAllocationStatus.Ignored),
    unavailable: countLinesByStatus(
      lineStats,
      KitRequestAllocationStatus.Unavailable
    ),
    expected: countLinesByStatus(
      lineStats,
      KitRequestAllocationStatus.Expected
    ),
    onHand: countLinesByStatus(lineStats, KitRequestAllocationStatus.OnHand),
    quarantined: countLinesByStatus(
      lineStats,
      KitRequestAllocationStatus.Quarantined
    ),
    readyToPack: countLinesByStatus(
      lineStats,
      KitRequestAllocationStatus.ReadyToPack
    ),
    packed: countLinesByStatus(lineStats, KitRequestAllocationStatus.Packed),
    shipped: countLinesByStatus(lineStats, KitRequestAllocationStatus.Shipped),
    delivered: countLinesByStatus(
      lineStats,
      KitRequestAllocationStatus.Delivered
    ),
    notInFacility: countLinesByStatus(
      lineStats,
      KitRequestAllocationStatus.NotInFacility
    ),
  };

  return {
    allocationStats,
    lineStats,
    requestStats,
  };
};

export const createDecoratedKitRequestLines = ({
  kitRequestLines,
  allocations,
  shipmentLines,
  handlingRequests,
  stockLots,
}: {
  kitRequestLines?: KitRequestLineDataLineLite[];
  allocations?: KitRequestLineDataAllocationLite[];
  shipmentLines?: ShipmentLineDataLite[];
  handlingRequests?: HandlingRequestDataLite[];
  stockLots?: StockLotDataLite[];
}) => {
  if (!kitRequestLines) return undefined;

  const allocationsByLineId = typedGroupBy(allocations || [], 'kitRequestLine');
  const shipmentLinesByLineId = typedGroupBy(
    shipmentLines || [],
    'kitRequestLine'
  );
  const handlingRequestsByLineId = typedGroupBy(
    handlingRequests || [],
    'kitRequestLine'
  );

  const stockLotsById = typedKeyBy(stockLots || [], 'id');

  const lineIdToStockLotIds: Record<string, Set<string>> = {};
  (allocations || []).forEach((allocation) => {
    if (allocation.kitRequestLine && allocation.stockLot?.id) {
      if (!lineIdToStockLotIds[allocation.kitRequestLine]) {
        lineIdToStockLotIds[allocation.kitRequestLine] = new Set();
      }
      lineIdToStockLotIds[allocation.kitRequestLine].add(
        allocation.stockLot.id
      );
    }
  });

  return kitRequestLines.map((line) => {
    const lineAllocations = allocationsByLineId[line.id] || [];
    const lineStockLotIds = lineIdToStockLotIds[line.id] || new Set();

    const lineStockLots = Array.from(lineStockLotIds)
      .map((id) => stockLotsById[id])
      .filter(Boolean);

    const allocatedQuantity = lineAllocations.reduce(
      (sum, allocation) => sum + (allocation.quant || 0),
      0
    );

    return {
      ...line,
      allocations: lineAllocations,
      shipmentLines: (shipmentLinesByLineId[line.id] || []).map(
        (shipmentLine) => ({
          ...shipmentLine,
          stockLot: stockLotsById[shipmentLine.stockLot],
        })
      ),
      handlingRequests: handlingRequestsByLineId[line.id] || [],
      stockLots: lineStockLots,
      allocatedQuantity,
    };
  }) as DecoratedKitRequestLine[];
};

export const combineDecoratedKitRequestLinesWithStatuses = ({
  decoratedKitRequestLines,
  kitRequestStatuses,
}: {
  decoratedKitRequestLines?: DecoratedKitRequestLine[];
  kitRequestStatuses: Record<
    KitRequestSchema['id'],
    CalculateKitRequestStatusReturn
  >;
}) => {
  if (!decoratedKitRequestLines) return [];

  return decoratedKitRequestLines.map((line) => {
    const statuses = kitRequestStatuses[line.kitRequest.id];
    if (!statuses) return line;
    return {
      ...line,
      stats: statuses.lineStats[line.id],
      allocations: line.allocations.map((allocation) => ({
        ...allocation,
        calculatedStatus: statuses.allocationStats[allocation.id],
      })),
    };
  });
};

export type EstimatedHandlingRequestData = {
  numberOfReels: number;
  numberOfShipmentLines: number;
  numberOfMerges: number;
  numberOfSplits: number;
};

// Estimate the number of handling requests to be generated
export const estimateHandlingRequestData = ({
  decoratedKitRequestLines,
  shipToLocation,
}: {
  decoratedKitRequestLines?: DecoratedKitRequestLine[];
  shipToLocation?: StockLocationSchemaLite | null;
}): EstimatedHandlingRequestData => {
  // Calculate number of reels based on kit request line count if location requires reels
  const numberOfReels = shipToLocation?.requiresReels
    ? decoratedKitRequestLines?.length ?? 0
    : 0;

  const numberOfShipmentLines = decoratedKitRequestLines?.length ?? 0;

  // Count merges for lines with multiple allocations if merges are required
  const numberOfMerges = shipToLocation?.requiresMerges
    ? decoratedKitRequestLines?.filter((line) => line.allocations.length > 1)
        .length
    : 0;

  // Count splits by checking if any allocation quantity is less than its stock lot quantity
  const numberOfSplits = shipToLocation?.requiresSplits
    ? decoratedKitRequestLines?.filter((line) =>
        line.allocations.some(
          (allocation) => allocation.quant < allocation.stockLot.quant
        )
      ).length
    : 0;

  const estimatedHandlingRequestData = {
    numberOfReels,
    numberOfShipmentLines,
    numberOfMerges: numberOfMerges ?? 0,
    numberOfSplits: numberOfSplits ?? 0,
  };

  return estimatedHandlingRequestData;
};

export const estimateHandlingRequestRemaining = ({
  decoratedKitRequestLines,
  shipToLocation,
}: {
  decoratedKitRequestLines?: DecoratedKitRequestLine[];
  shipToLocation?: StockLocationSchemaLite | null;
}): EstimatedHandlingRequestData => {
  if (!decoratedKitRequestLines) {
    return {
      numberOfReels: 0,
      numberOfShipmentLines: 0,
      numberOfMerges: 0,
      numberOfSplits: 0,
    };
  }

  const filteredLines = decoratedKitRequestLines.filter(
    (line) =>
      !line.stats?.calculatedStatuses?.includes(
        KitRequestAllocationStatus.NotInFacility
      )
  );

  const linesNeedingReels = filteredLines.filter(
    (line) =>
      line.stockLots.length > 0 &&
      line.stockLots.some((stockLot) =>
        NON_REEL_TAPE_PACKAGES.includes(stockLot.package)
      )
  );

  const linesNeedingShipmentLines = filteredLines.filter(
    (line) => line.stockLots.length > 0 && line.shipmentLines.length === 0
  );

  const linesNeedingMerges = filteredLines.filter(
    (line) => line.allocations.length > 1
  );

  const linesNeedingSplits = filteredLines.filter((line) =>
    line.allocations.some(
      (allocation) => allocation.quant < allocation.stockLot.quant
    )
  );

  const numberOfReels = shipToLocation?.requiresReels
    ? linesNeedingReels.length
    : 0;

  const numberOfShipmentLines = linesNeedingShipmentLines.length;

  const numberOfMerges = shipToLocation?.requiresMerges
    ? linesNeedingMerges.length
    : 0;

  const numberOfSplits = shipToLocation?.requiresSplits
    ? linesNeedingSplits.length
    : 0;

  const estimatedHandlingRequestData = {
    numberOfReels,
    numberOfShipmentLines,
    numberOfMerges,
    numberOfSplits,
  };

  return estimatedHandlingRequestData;
};
